<template>
  <div
    class="ag-cell-label-container"
    role="presentation"
    data-testid="deletable-column-header"
  >
    <!-- Yes, `.ag-cell-label-container` arranges these items with `flex-direction: row-reverse`. Because reasons, I'm
    sure. We still should use it to keep layouts consistent with default AG Grid headers. -->

    <!-- No delete, filter button only -->
    <span
      v-if="!params.allowDelete"
      role="presentation"
      :class="{ 'ag-filter-active': filterActive }"
    >
      <span
        class="ag-icon ag-icon-filter ag-header-cell-filter-button"
        aria-hidden="true"
        role="presentation"
        unselectable="on"
        @click.stop="onFilterClicked"
      />
    </span>

    <!-- Delete allowed, use dropdown for more options -->
    <div
      v-else
      role="presentation"
      @click.stop
    >
      <v-menu
        location="bottom start"
        role="presentation"
        :close-on-content-click="true"
        :offset="[0, 4]"
      >
        <!-- This component will be teleported elsewhere and therefore will not inherit the AG Grid styles. In order to
         keep the icons working, several of the AG Grid classes must be applied to the list component -->
        <template #activator="{ props: menuProps }">
          <v-btn
            ref="menuButton"
            v-bind="menuProps"
            icon
            size="x-small"
            variant="text"
            aria-label="Column options"
          >
            <span
              class="ag-icon ag-icon-menu-alt"
              role="presentation"
              unselectable="on"
            />
          </v-btn>
        </template>

        <v-list density="compact">
          <v-list-item @click="onFilterClicked">
            <template #prepend>
              <div
                class="ag-cell-label-container menu-icon ag-theme-iconSet-5"
                style="--ag-icon-size: 16px;"
                :class="{ 'ag-filter-active': filterActive }"
                role="presentation"
              >
                <span class="ag-icon ag-icon-filter" />
              </div>
            </template>
            <v-list-item-title>Filter</v-list-item-title>
          </v-list-item>

          <v-list-item @click="onDeleteColumn">
            <template #prepend>
              <TrashIcon
                class="menu-icon"
                width="16"
                height="16"
              />
            </template>
            <v-list-item-title>Delete Column</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>

    <!-- Column text and sort arrow container - clicking anywhere in here triggers sorting -->
    <div
      class="ag-header-cell-label"
      role="presentation"
      @click="onSortRequested"
    >
      <span class="ag-header-cell-text">{{ params.displayName }}</span>

      <!-- Sort arrows - sticks to text like default AG Grid header -->
      <span
        v-if="sortState === 'asc'"
        class="ag-icon ag-sort-indicator-icon ag-icon-asc"
        role="presentation"
        unselectable="on"
        aria-hidden="true"
      />
      <span
        v-else-if="sortState === 'desc'"
        class="ag-icon ag-sort-indicator-icon ag-icon-desc"
        role="presentation"
        unselectable="on"
        aria-hidden="true"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue';
import type {IHeader, IHeaderParams} from 'ag-grid-community';
import TrashIcon from '@/components/svgs/TrashIcon.vue';

export interface DeletableColumnHeaderParams extends IHeaderParams {
  onDeleteColumn: (colId: string) => void;
  allowDelete: boolean;
}

export default defineComponent({
  name: 'DeletableColumnHeader',
  components: {TrashIcon},
  props: {
    params: {
      type: Object as PropType<DeletableColumnHeaderParams>,
      required: true,
    },
  },
  data() {
    return {
      sortState: null as 'asc' | 'desc' | null,
      filterActive: false,
    };
  },
  mounted() {
    this.sortState = this.params.column.getSort() ?? null;
    this.filterActive = this.params.column.isFilterActive();

    if (this.params.api) {
      this.params.column.addEventListener('sortChanged', this.onSortChanged);
      this.params.column.addEventListener('filterChanged', this.onFilterChanged);
    }
  },
  beforeUnmount() {
    if (this.params.api) {
      this.params.column.removeEventListener('sortChanged', this.onSortChanged);
      this.params.column.removeEventListener('filterChanged', this.onFilterChanged);
    }
  },
  methods: {
    onSortChanged() {
      this.sortState = this.params.column.getSort() ?? null;
    },
    onFilterChanged() {
      this.filterActive = this.params.column.isFilterActive();
    },
    onSortRequested(event: MouseEvent) {
      if (!this.params.enableSorting) return;
      this.params.progressSort(event.shiftKey);
    },
    onFilterClicked() {
      this.params.api.showColumnFilter(this.params.column.getColId());
    },
    onDeleteColumn() {
      this.params.onDeleteColumn(this.params.column.getColId());
    },
  },
}) as unknown as IHeader;
</script>

<style scoped lang="scss">
.menu-icon {
  margin-right: 8px;
}
</style>
